import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Image as ImageIcon,
  Loader2,
  X,
  Upload,
  Save,
  Eye,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useLandlordStore } from "@/stores/useLandlordStore";
import { LoadingScreen } from "@/components/common/LoadingSpinner";
import apiClient from "@/services/apiClient";
import { useToast } from "@/contexts/ToastContext";

export const EditVacancy: React.FC = () => {
  const navigate = useNavigate();
  const { vacancyId } = useParams<{ vacancyId: string }>();
  const location = useLocation();
  const { properties, fetchProperties, vacancies, fetchVacancies } = useLandlordStore();
  const { warning, success, error: toastError, info } = useToast();
  
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const [availableUnits, setAvailableUnits] = useState<any[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string>("");
  
  const [postTitle, setPostTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [availableDate, setAvailableDate] = useState("");
  const [rentAmount, setRentAmount] = useState<number>(0);
  
  const [isLoadingProps, setIsLoadingProps] = useState(false);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingVacancy, setIsLoadingVacancy] = useState(true);

  // Load vacancy data for editing
  useEffect(() => {
    const loadVacancyData = async () => {
      if (!vacancyId) {
        toastError('Error', 'No vacancy ID provided');
        navigate('/landlord/vacancies');
        return;
      }

      setIsLoadingVacancy(true);
      try {
        // Find vacancy in store or fetch from API
        let vacancy = vacancies.find(v => v.id === vacancyId);
        
        if (!vacancy) {
          // Fetch from API if not in store
          const response = await apiClient.get(`/vacancies/${vacancyId}`);
          vacancy = response.data;
        }

        if (vacancy) {
          setSelectedPropertyId(vacancy.property_id || '');
          setSelectedUnitId(vacancy.unit_id || '');
          setPostTitle(vacancy.title || '');
          setDescription(vacancy.description || '');
          setImages(vacancy.images || []);
          setAvailableDate(vacancy.available_date ? new Date(vacancy.available_date).toISOString().split('T')[0] : '');
          setRentAmount(vacancy.rent_amount || 0);

          // Load property and unit data
          if (vacancy.property_id && properties.length === 0) {
            setIsLoadingProps(true);
            await fetchProperties();
            setIsLoadingProps(false);
          }

          // Load units for the selected property
          if (vacancy.property_id) {
            setIsLoadingUnits(true);
            try {
              const response = await apiClient.get(`/units/property/${vacancy.property_id}`);
              const units = response.data.filter((u: any) => u.status === "Available" || u.id === vacancy.unit_id);
              setAvailableUnits(units);
            } catch (error) {
              console.error("Could not fetch units:", error);
              toastError('Failed to load units', 'Error fetching units for this property.');
            } finally {
              setIsLoadingUnits(false);
            }
          }
        } else {
          toastError('Not found', 'Vacancy not found');
          navigate('/landlord/vacancies');
        }
      } catch (error) {
        console.error("Failed to load vacancy:", error);
        toastError('Error', 'Failed to load vacancy data');
        navigate('/landlord/vacancies');
      } finally {
        setIsLoadingVacancy(false);
      }
    };

    loadVacancyData();
  }, [vacancyId]);

  useEffect(() => {
    if (properties.length === 0 && !selectedPropertyId) {
      setIsLoadingProps(true);
      fetchProperties().finally(() => setIsLoadingProps(false));
    }
  }, [properties.length, fetchProperties, selectedPropertyId]);

  const handlePropertyChange = async (propertyId: string) => {
    setSelectedPropertyId(propertyId);
    setSelectedUnitId("");
    setAvailableUnits([]);
    
    if (!propertyId) return;

    setIsLoadingUnits(true);
    try {
      const response = await apiClient.get(`/units/property/${propertyId}`);
      const units = response.data.filter((u: any) => u.status === "Available");
      setAvailableUnits(units);
    } catch (error) {
      console.error("Could not fetch units:", error);
      toastError('Failed to load units', 'Error fetching units for this property.');
    } finally {
      setIsLoadingUnits(false);
    }
  };

  const handleUnitChange = (unitId: string) => {
    setSelectedUnitId(unitId);
    const currProp = properties.find((p) => p.id === selectedPropertyId);
    const currUnit = availableUnits.find((u) => u.id === unitId);
    if (currProp && currUnit) {
      setPostTitle(`${currUnit.unit_number} in ${currProp.name}`);
      setRentAmount(currUnit.rent_amount || 0);
    } else {
      setPostTitle("");
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages = Array.from(e.target.files).map((f) =>
        URL.createObjectURL(f)
      );
      setImages((prev) => [...prev, ...newImages]);
    }
  };

  const removeImage = (uri: string) => {
    setImages(images.filter((image) => image !== uri));
  };

  const handlePreview = () => {
    if (!selectedPropertyId || !selectedUnitId || !postTitle) {
      warning('Incomplete form', 'Please select a property, unit, and add a title before previewing.');
      return;
    }

    const selectedUnit = availableUnits.find(u => u.id === selectedUnitId);
    const previewData = {
      id: vacancyId,
      property_id: selectedPropertyId,
      unit_id: selectedUnitId,
      title: postTitle,
      description,
      rent_amount: rentAmount || selectedUnit?.rent_amount,
      available_date: availableDate,
      images,
      property_name: properties.find(p => p.id === selectedPropertyId)?.name,
      unit_name: selectedUnit?.unit_number,
      bedrooms: (selectedUnit as any)?.bedrooms,
      bathrooms: (selectedUnit as any)?.bathrooms,
      amenities: (selectedUnit as any)?.amenities,
      isEdit: true,
    };

    navigate('/landlord/vacancies/preview', { state: { vacancy: previewData } });
  };

  const handleSave = async () => {
    if (!selectedPropertyId || !selectedUnitId || !postTitle) {
      warning('Incomplete form', 'Please ensure all required fields are filled.');
      return;
    }

    const selectedUnit = availableUnits.find(u => u.id === selectedUnitId);
    if (!selectedUnit) return;

    setIsSaving(true);
    
    try {
      // Upload images if new ones were added (mock upload for now)
      const imageKeys = images;

      await apiClient.patch(`/vacancies/${vacancyId}`, {
        propertyId: selectedPropertyId,
        unitId: selectedUnitId,
        title: postTitle,
        description,
        rentAmount: rentAmount || selectedUnit.rent_amount,
        availableDate: availableDate,
        imageKeys,
      });

      success('Vacancy updated!', 'Your changes have been saved successfully.');
      navigate("/landlord/vacancies");
    } catch (error) {
      console.error("Failed to update vacancy:", error);
      toastError('Update failed', 'Failed to update vacancy. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingVacancy || (isLoadingProps && properties.length === 0)) {
    return <LoadingScreen />;
  }

  const inputClass =
    "w-full px-4 py-3 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text placeholder:text-light-subtle dark:text-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow";
  const labelClass =
    "block text-sm font-semibold text-light-text dark:text-dark-text mb-2";

  return (
    <div className="max-w-3xl mx-auto pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
        >
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="text-xl font-bold text-light-text dark:text-dark-text">
          Edit Vacancy
        </h1>
      </div>

      <div className="bg-light-card dark:bg-dark-card p-6 md:p-8 rounded-xl shadow-sm border border-light-border dark:border-dark-border space-y-8">
        {/* Selection Area */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
            Property & Unit
          </h2>
          <div>
            <label className={labelClass}>Select Property</label>
            <select
              value={selectedPropertyId}
              onChange={(e) => handlePropertyChange(e.target.value)}
              className={inputClass}
            >
              <option value="">-- Choose Property --</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>
              Select Unit{" "}
              {isLoadingUnits && <span className="text-xs text-primary ml-2 italic">Loading...</span>}
            </label>
            <select
              value={selectedUnitId}
              onChange={(e) => handleUnitChange(e.target.value)}
              className={inputClass}
              disabled={!selectedPropertyId || isLoadingUnits || availableUnits.length === 0}
            >
              <option value="">-- Choose Unit --</option>
              {availableUnits.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.unit_number} - KES {u.rent_amount?.toLocaleString() || 0}
                </option>
              ))}
            </select>
            {selectedPropertyId && !isLoadingUnits && availableUnits.length === 0 && (
              <p className="text-sm text-amber-600 mt-2">No available units found at this property.</p>
            )}
          </div>
        </div>

        {/* Input Text Form */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
            Details
          </h2>
          <div>
            <label className={labelClass}>Vacancy Title</label>
            <input
              type="text"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              placeholder="e.g. Stunning 2BR Apartment"
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the unit..."
              rows={4}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className={labelClass}>Available Date</label>
            <input
              type="date"
              value={availableDate}
              onChange={(e) => setAvailableDate(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Rent Amount (KES/month)</label>
            <input
              type="number"
              value={rentAmount}
              onChange={(e) => setRentAmount(Number(e.target.value))}
              placeholder="Enter rent amount"
              className={inputClass}
            />
            <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">
              Current unit rent: KES {availableUnits.find(u => u.id === selectedUnitId)?.rent_amount?.toLocaleString() || 0}
            </p>
          </div>
        </div>

        {/* Image Uploader */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
            Photos ({images.length} uploaded)
          </h2>
          <div className="flex flex-wrap gap-4">
            {images.map((uri) => (
              <div
                key={uri}
                className="relative w-24 h-24 rounded-lg overflow-hidden border border-light-border dark:border-dark-border"
              >
                <img
                  src={uri}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(uri)}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-1"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ))}

            <label className="flex flex-col items-center justify-center w-24 h-24 rounded-lg border-2 border-dashed border-light-border dark:border-dark-border cursor-pointer hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
              <Upload size={24} className="text-light-subtle dark:text-dark-subtle mb-1" />
              <span className="text-[10px] text-light-subtle dark:text-dark-subtle font-medium uppercase text-center leading-tight">Add<br />Images</span>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 flex gap-4">
          <button
            type="button"
            className="flex-1 py-3 text-center bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-xl font-bold text-base hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            onClick={handlePreview}
          >
            <Eye size={20} />
            Preview
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={handleSave}
            className="flex-1 py-3 text-center bg-primary text-white rounded-xl font-bold text-base hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2"
          >
            {isSaving && <Loader2 className="animate-spin" size={20} />}
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};